/* Copyright (c) 2025-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */
import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';

import { TranslateModule } from '@ngx-translate/core';
import { AutoFocusDirective } from '../../directives/auto-focus.directive';
import { NvFocusRippleDirective } from '../../directives/focus-ripple.directive';
import { NvHigTableColumns } from '../../models/hig-modal-shared.types';

@Component({
    selector: 'nv-hig-table-comparison',
    templateUrl: './nv-hig-table_comparison.component.html',
    styleUrls: ['./nv-hig-table_comparison.component.scss'],
    imports: [MatIconModule, MatButtonModule, TranslateModule, NvFocusRippleDirective, AutoFocusDirective]
})
export class NvHigTableComparisonComponent implements OnChanges {
    @Input() ariaLabel: string;
    private tablePrefix: string = 'table-comparison';
    private _columns: NvHigTableColumns[] = [];
    @Input() set columns(value: NvHigTableColumns[]) {
        this._columns = value ?? [];
        this.recomputeDerivedState();
    }
    get columns(): NvHigTableColumns[] {
        return this._columns;
    }
    @Output() columnActionClick = new EventEmitter<{ column: NvHigTableColumns; columnIndex: number }>();

    // Precomputed identifiers and ARIA helpers
    colHeaderIds: string[] = [];
    cellIdsByColumn: string[][] = [];
    ctaCellIds: string[] = [];
    rowCount: number = 0;
    hasAnyCta: boolean = false;
    rowIndexes: number[] = [];
    headerRowOwns: string = '';
    rowOwnsByIndex: string[] = [];

    constructor() {}

    onButtonClick(column: NvHigTableColumns, columnIndex: number): void {
        this.columnActionClick.emit({ column, columnIndex });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if ('ariaLabel' in changes) {
            this.recomputeDerivedState();
        }
    }

    private recomputeDerivedState(): void {
        const columns = this._columns ?? [];
        this.hasAnyCta = columns.some(column => !!column.cta);

        const maximumCellCount = columns.reduce((maxCellCountSoFar, column) => {
            const cellCount = column?.cells?.length ?? 0;
            return cellCount > maxCellCountSoFar ? cellCount : maxCellCountSoFar;
        }, 0);

        this.rowCount = this.hasAnyCta ? maximumCellCount + 1 : maximumCellCount;

        // IDs used directly in the DOM
        this.colHeaderIds = columns.map((_, columnIndex) => `${this.tablePrefix}-col-${columnIndex}`);
        this.cellIdsByColumn = columns.map((column, columnIndex) =>
            (column?.cells ?? []).map((_, rowIndex) => `${this.tablePrefix}-cell-${rowIndex}-${columnIndex}`)
        );
        this.ctaCellIds = columns.map((_, columnIndex) => `${this.tablePrefix}-cell-cta-${columnIndex}`);

        // Derived ARIA helpers
        const totalRowCount = this.rowCount;
        this.rowIndexes = totalRowCount > 0 ? Array.from({ length: totalRowCount }, (_, rowIndex) => rowIndex) : [];
        this.headerRowOwns = this.colHeaderIds.join(' ');
        //eslint-disable-next-line @typescript-eslint/no-shadow
        this.rowOwnsByIndex = Array.from({ length: totalRowCount }, (_, rowIndex) => {
            const isCtaRow = this.hasAnyCta && rowIndex === totalRowCount - 1;
            if (isCtaRow) return this.ctaCellIds.join(' ');
            //eslint-disable-next-line @typescript-eslint/no-shadow
            return columns.map((_, columnIndex) => `${this.tablePrefix}-cell-${rowIndex}-${columnIndex}`).join(' ');
        });
    }
}
